import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { m } from 'framer-motion';
// @mui
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { LoadingScreen } from 'src/components/loading-screen';
// assets
import { ForbiddenIllustration } from 'src/assets/illustrations';
// components
import { MotionContainer, varBounce } from 'src/components/animate';
import { useSelector } from 'react-redux';

// ----------------------------------------------------------------------

export default function RoleBasedGuard({
  hasContent,
  children,
  currentPage,
  sx,
  screenName = '',
}) {
  const { screens = {}, loading: permissionLoading, initialized: permissionInitialized } = useSelector(
    (state) => state.permission || {}
  );
  const normalizedScreens = useMemo(
    () =>
      Object.entries(screens).reduce((acc, [key, value]) => {
        acc[key.trim().toLowerCase()] = value;
        return acc;
      }, {}),
    [screens]
  );
  const resolvedPermission = useMemo(
    () => normalizedScreens[screenName?.trim()?.toLowerCase()] || null,
    [normalizedScreens, screenName]
  );
  const canAccessRestrictedPages = currentPage === 'list' || currentPage === 'view';
  const hasFullAccess = Boolean(resolvedPermission?.fullAccess);
  const hasPermission = Boolean(resolvedPermission);
  const isLoading = !permissionInitialized || permissionLoading;

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!hasPermission) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: 'center', ...sx }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            Permission Denied
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            You do not have permission to access this page
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <ForbiddenIllustration
            sx={{
              mt: { xs: 1, sm: 2 },
              mb: { xs: 5, sm: 10 },
            }}
          />
        </m.div>
      </Container>
    ) : null;
  }

  if (!hasFullAccess && !canAccessRestrictedPages && !isLoading) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: 'center', ...sx }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            Permission Denied
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            You do not have permission to access this page
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <ForbiddenIllustration
            sx={{
              mt: { xs: 1, sm: 2 },
              mb: { xs: 5, sm: 10 },
            }}
          />
        </m.div>
      </Container>
    ) : null;
  }

  return <>{React.cloneElement(children, { fullAccess: hasFullAccess })}</>;
}

RoleBasedGuard.propTypes = {
  children: PropTypes.node,
  hasContent: PropTypes.bool,
  sx: PropTypes.object,
  currentPage: PropTypes.string,
  screenName: PropTypes.string,
};
